import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost } from '../lib/api';
import { CART_EVENT, getCartToken, storeCartToken } from '../lib/cartToken';
import { AUTH_TOKEN_EVENT } from '../lib/authToken';

const CartContext = createContext(null);

const emptySummary = {
  item_count: 0,
  subtotal: 0,
  total: 0,
};

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function CartProvider({ children }) {
  const [cartSummary, setCartSummary] = useState(emptySummary);
  const [loading, setLoading] = useState(false);

  const applyPayload = useCallback((response) => {
    if (response?.cart_token) {
      storeCartToken(response.cart_token);
    }

    const summary = response?.data?.summary || {};
    const items = response?.data?.items || [];
    const itemCount =
      toNumber(summary.item_count) ||
      items.reduce((sum, item) => sum + toNumber(item.quantity), 0);

    setCartSummary({
      item_count: itemCount,
      subtotal: toNumber(summary.subtotal),
      total: toNumber(summary.total),
    });
  }, []);

  const syncCart = useCallback(async () => {
    try {
      setLoading(true);
      getCartToken();
      const response = await apiGet('/cart', { auth: true });
      applyPayload(response);
    } catch {
      setCartSummary(emptySummary);
    } finally {
      setLoading(false);
    }
  }, [applyPayload]);

  useEffect(() => {
    let idleId;
    let timeoutId;

    const start = () => {
      syncCart();
    };

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(start, { timeout: 2000 });
    } else {
      timeoutId = window.setTimeout(start, 400);
    }

    const refresh = () => {
      syncCart();
    };

    window.addEventListener(CART_EVENT, refresh);
    window.addEventListener(AUTH_TOKEN_EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      if (idleId != null && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleId);
      }
      window.clearTimeout(timeoutId);
      window.removeEventListener(CART_EVENT, refresh);
      window.removeEventListener(AUTH_TOKEN_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, [syncCart]);

  const addToCart = useCallback(
    async ({ productId, variantId = null, quantity = 1, price = null }) => {
      if (!productId) {
        throw new Error('Product is required.');
      }

      const response = await apiPost(
        '/cart/items',
        {
          product_id: productId,
          product_variant_id: variantId,
          quantity,
          price,
          cart_token: getCartToken(),
        },
        { auth: true }
      );

      await syncCart();
      return response;
    },
    [syncCart]
  );

  const value = useMemo(
    () => ({
      cartCount: cartSummary.item_count,
      cartSummary,
      loading,
      syncCart,
      addToCart,
    }),
    [cartSummary, loading, syncCart, addToCart]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error('useCart must be used within CartProvider');
  }
  return ctx;
}

export default CartContext;
